import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, AlertTriangle, CheckCircle, DollarSign, Trash2, Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspiciousUsers, setSuspiciousUsers] = useState([]);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  });

  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('admin_token');
      setAdminToken('');
      toast.error('Session admin expirée, veuillez vous reconnecter');
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (adminToken) {
      loadStats();
      loadSuspiciousUsers();
    } else {
      setLoading(false);
    }
  }, [adminToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', response.data.token);
      setAdminToken(response.data.token);
      setPassword('');
      setLoading(true);
      toast.success('Accès admin autorisé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mot de passe incorrect');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken('');
    toast.success('Déconnecté du panneau admin');
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, authConfig());
      setStats(response.data);
    } catch (error) {
      if (!handleAuthError(error)) {
        toast.error('Erreur de chargement des statistiques');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSuspiciousUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/suspicious-locations`, authConfig());
      setSuspiciousUsers(response.data);
    } catch (error) {
      if (!handleAuthError(error)) {
        console.error('Error loading suspicious users:', error);
      }
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeletingUserId(userToDelete.id);
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, authConfig());
      
      toast.success(`Utilisateur ${userToDelete.name} supprimé avec succès`);
      
      // Refresh data
      await loadSuspiciousUsers();
      await loadStats();
      
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      if (!handleAuthError(error)) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  if (!adminToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-screen">
        <div className="glass-card p-8 max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <div className="p-4 bg-pink-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-pink-600" />
            </div>
            <h1 className="text-2xl font-bold gradient-text">Accès Administrateur</h1>
            <p className="text-gray-600 text-sm mt-2 text-center">
              Cette zone est protégée. Veuillez saisir le mot de passe admin.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe admin"
              data-testid="admin-password-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400"
              autoFocus
            />
            <button
              type="submit"
              disabled={loggingIn || !password}
              data-testid="admin-login-button"
              className="w-full px-4 py-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loggingIn ? 'Connexion...' : (<><Lock className="w-4 h-4" /> Se connecter</>)}
            </button>
          </form>
          <button
            onClick={() => navigate(`/${language}`)}
            className="w-full mt-4 text-sm text-gray-500 hover:text-pink-600"
            style={{ transition: 'color 0.2s' }}
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(`/${language}/dashboard`)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600"
            style={{ transition: 'color 0.2s' }}
          >
            <ArrowLeft className="w-5 h-5" />
            Retour au Dashboard
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-gray-600 hover:text-red-600"
            style={{ transition: 'color 0.2s' }}
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold gradient-text mb-2">Tableau de Bord Admin</h1>
          <p className="text-gray-600">Vue d'ensemble des statistiques de la plateforme</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.total_users || 0}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Utilisateurs Inscrits</h3>
            <p className="text-xs text-gray-500">Total depuis le lancement</p>
          </div>

          {/* Free Signups Used */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.free_signups || 0}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Inscriptions Gratuites</h3>
            <p className="text-xs text-gray-500">Sur 500 disponibles</p>
          </div>

          {/* Remaining Free */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.remaining_free || 0}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Places Gratuites Restantes</h3>
            <p className="text-xs text-gray-500">Avant passage au payant</p>
          </div>

          {/* Suspicious Locations */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-3xl font-bold text-gray-800">{stats?.suspicious_locations || 0}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Localisations Suspectes</h3>
            <p className="text-xs text-gray-500">Nécessitent une vérification</p>
          </div>
        </div>

        {/* Payment Status */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-6 h-6 text-pink-500" />
            <h2 className="text-xl font-bold text-gray-800">Statut des Inscriptions</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Progression (500 gratuits)</span>
                <span className="text-sm font-bold text-gray-800">
                  {((stats?.free_signups || 0) / 500 * 100).toFixed(1)}%
                </span>
              </div>
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-green-600"
                  style={{ width: `${Math.min((stats?.free_signups || 0) / 500 * 100, 100)}%` }}
                />
              </div>
            </div>

            <div className="flex flex-col justify-center">
              {stats?.remaining_free > 0 ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Inscriptions gratuites disponibles</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-orange-600">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Paiement requis (10$ USD)</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Suspicious Users Table */}
        {suspiciousUsers.length > 0 && (
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-bold text-gray-800">Utilisateurs avec Localisation Suspecte</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Nom</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Localisation Déclarée</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Localisation IP</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Distance</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Niveau</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {suspiciousUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800">{user.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{user.location}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {user.location_verification?.ip_country || 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {user.location_verification?.distance 
                          ? `${Math.round(user.location_verification.distance)} km`
                          : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          user.location_verification?.suspicion_level === 'high'
                            ? 'bg-red-100 text-red-700'
                            : user.location_verification?.suspicion_level === 'medium'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {user.location_verification?.suspicion_level || 'low'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleDeleteClick(user)}
                          disabled={deletingUserId === user.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                        >
                          {deletingUserId === user.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                              Suppression...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3 h-3" />
                              Supprimer
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Confirmer la suppression</h3>
              </div>

              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{userToDelete.name}</strong> ({userToDelete.email}) ?
                <br /><br />
                Cette action est <strong>irréversible</strong> et supprimera :
              </p>

              <ul className="list-disc pl-5 mb-6 text-sm text-gray-600 space-y-1">
                <li>Le profil utilisateur</li>
                <li>Toutes les photos</li>
                <li>Tous les matchs</li>
                <li>Tous les messages</li>
                <li>Toutes les évaluations</li>
                <li>L'historique de paiement</li>
              </ul>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  disabled={deletingUserId === userToDelete.id}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deletingUserId === userToDelete.id}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors font-medium"
                >
                  {deletingUserId === userToDelete.id ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
